import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { IdView, Typography } from '../../components';
import { Card, CardBody, Chip, Avatar, Snippet } from '@heroui/react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslations } from '../../contexts/translation.context';
import {
  CalendarIcon,
  DoubleArrowRightIc,
  EmptyContentClientIc,
  EthIc,
  UsdcCoinIc,
} from '../../components/icons';
import { UniversalTable } from '../../components/AccountTable';
import CustomHeroButton from '../../components/button';
import CustomHeroInput from '../../components/CustomHeroInput';
import clientAuthService from '../../api/client/clientAuthService';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';

interface PartnerInfo {
  id?: string;
  avatar?: string;
  name?: string;
  username?: string;
}

interface CompanyInfo {
  id?: string;
  link?: string;
  name?: string;
  created_at?: string;
  subscribed_count?: number;
  need_subs?: number;
  avatar?: string;
  username?: string;
  [key: string]: any;
}

interface InvoiceInfo {
  id?: string;
  status?: string;
  number?: string | number;
  reason?: string;
  comment?: string;
  partner?: PartnerInfo;
  order?: {
    id?: string | number;
    number?: string | number;
    [key: string]: any;
  };
  [key: string]: any;
}

interface TransactionInfo {
  amount?: number;
  admin?: string;
  id?: string;
  number?: string | number;
  [key: string]: any;
}

// Типы для API ответа
interface BalanceHistoryItem {
  created_at: string;
  id: string;
  before: number;
  after: number;
  amount: number;
  transaction?: TransactionInfo;
  invoice?: InvoiceInfo;
  previous?: string;
  owner?: string;
  company?: CompanyInfo;
  description?: string;
  comment?: string;
  reason?: string;
  details?: string;
  note?: string;
  [key: string]: any;
}

// Обновляем интерфейс для API ответа
interface BalanceHistoryResponse {
  page: number;
  page_size: number;
  count: number;
  objects: any[];
}

// Тип для ответа создания платежного инвойса
interface PaymentInvoiceResponse {
  crypto_amount: string;
  crypto_currency: string;
  crypto_address: string;
  network: string;
  expiration_estimate_date: string;
}

type DepositFormValues = {
  amount?: number | '';
  currency: string;
};

type WalletActionType =
  | 'reserve'
  | 'reserve_revert'
  | 'campaign_complete'
  | 'invoice_payment'
  | 'balance_top_up'
  | 'guarantee'
  | 'balance_change';

const parseToNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.replace(',', '.').trim();
    if (!normalized) {
      return 0;
    }

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const formatCurrencyValue = (amount: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const extractHandleFromLink = (link?: string): string | undefined => {
  if (!link) {
    return undefined;
  }

  try {
    const url = new URL(link);
    const [firstSegment] = url.pathname.split('/').filter(Boolean);
    if (firstSegment) {
      return firstSegment.startsWith('@') ? firstSegment : `@${firstSegment}`;
    }
  } catch {
    const match = link.match(/onlyfans\.com\/([^/?#]+)/i);
    if (match?.[1]) {
      const username = match[1];
      return username.startsWith('@') ? username : `@${username}`;
    }
  }

  return undefined;
};

const getDisplayIdentifier = (
  ...values: Array<string | number | undefined>
): string | undefined => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const stringValue = typeof value === 'number' ? value.toString() : value;
    const trimmed = stringValue.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return undefined;
};

const pickFirstString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return undefined;
};

const normalizeBalanceHistoryItems = (items: any[] | undefined): BalanceHistoryItem[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => {
    const normalizedId =
      typeof item?.id === 'string'
        ? item.id
        : item?.id !== undefined && item?.id !== null
          ? String(item.id)
          : '';

    const invoice = item?.invoice
      ? {
          ...item.invoice,
          id:
            typeof item.invoice.id === 'string'
              ? item.invoice.id
              : item.invoice.id !== undefined && item.invoice.id !== null
                ? String(item.invoice.id)
                : undefined,
        }
      : undefined;

    const transaction = item?.transaction
      ? {
          ...item.transaction,
          id:
            typeof item.transaction.id === 'string'
              ? item.transaction.id
              : item.transaction.id !== undefined && item.transaction.id !== null
                ? String(item.transaction.id)
                : undefined,
          amount: parseToNumber(item.transaction.amount ?? item.amount),
        }
      : undefined;

    return {
      ...item,
      id: normalizedId,
      invoice,
      transaction,
      before: parseToNumber(item?.before),
      after: parseToNumber(item?.after),
      amount: parseToNumber(item?.amount),
    };
  });
};

const Wallet: React.FC = () => {
  const { user } = useAuth();
  const { t, lang } = useTranslations();
  const [balanceHistoryData, setBalanceHistoryData] = useState<BalanceHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentInvoice, setPaymentInvoice] = useState<PaymentInvoiceResponse | null>(null);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false); // Флаг для предотвращения бесконечных запросов
  const [currentTime, setCurrentTime] = useState(new Date()); // Состояние для отслеживания текущего времени
  const [selectedCurrency, setSelectedCurrency] = useState('usdttrc20');
  const formikRef = useRef<FormikProps<DepositFormValues> | null>(null);

  const HARDCODED_MIN = 100;

  const getCurrencyLimits = useCallback(
    (_currency: string) => {
      return { min: HARDCODED_MIN, max: undefined as number | undefined };
    },
    []
  );

  const currentLimits = useMemo(
    () => getCurrencyLimits(selectedCurrency),
    [getCurrencyLimits, selectedCurrency]
  );

  const getMinErrorText = useCallback(
    (min: number) =>
      t('wallet.amountLessThanMin', {
        min: `$${formatCurrencyValue(min)}`,
      }),
    [t]
  );

  const getMaxErrorText = useCallback(
    (max: number) =>
      t('wallet.amountGreaterThanMax', {
        max: `$${formatCurrencyValue(max)}`,
      }),
    [t]
  );
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard'), href: '/' },
      { label: t('sidebar.wallet'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);
  // Состояния для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState<
    | {
        page: number;
        page_size: number;
        count: number;
      }
    | undefined
  >(undefined);


  // Функция для получения истории баланса
  const fetchBalanceHistory = async (page: number = 1, size: number = 10) => {
    if (hasError) return; // Не делаем запрос если уже была ошибка

    try {
      setIsLoading(true);
      setError(null);
      const response = await clientAuthService.getBalanceHistory({
        page: page,
        page_size: size,
      });
      if (response.data) {
        const data: BalanceHistoryResponse = response.data;
        setBalanceHistoryData(normalizeBalanceHistoryItems(data.objects));
        setPagination({
          page: data.page,
          page_size: data.page_size,
          count: data.count,
        });
        setCurrentPage(data.page);
        setPageSize(data.page_size);
        setHasError(false); // Сбрасываем флаг ошибки при успехе
      }
    } catch (error) {
      console.error('Ошибка при получении истории баланса:', error);
      setError(t('wallet.failedLoadHistory'));
      setHasError(true); // Устанавливаем флаг ошибки
    } finally {
      setIsLoading(false);
    }
  };

  // Автообновление каждые 10 секунд только если нет ошибок
  useEffect(() => {
    fetchBalanceHistory(currentPage, pageSize); // Первоначальная загрузка

    const interval = setInterval(() => {
      if (!hasError) {
        // Проверяем флаг ошибки перед запросом
        fetchBalanceHistory(currentPage, pageSize);
      }
    }, 10000); // 10 секунд

    return () => clearInterval(interval);
  }, [hasError, currentPage, pageSize]);

  // Таймер для обновления времени истечения каждую секунду
  useEffect(() => {
    if (paymentInvoice) {
      const timer = setInterval(() => {
        setCurrentTime(new Date());

        // Проверяем, истекло ли время
        const date = new Date(paymentInvoice.expiration_estimate_date);
        if (date.getTime() <= currentTime.getTime()) {
          // Время истекло, очищаем информацию о платеже
          setPaymentInvoice(null);
          // setInvoiceError('Время для оплаты истекло. Создайте новый платежный инвойс.');
        }
      }, 1000); // Обновляем каждую секунду

      return () => clearInterval(timer);
    }
  }, [paymentInvoice, currentTime]);

  // Функция для создания платежного инвойса
  const createPaymentInvoice = async (currency: string) => {
    const formikInstance = formikRef.current;
    const limits = getCurrencyLimits(currency);
    const effectiveMin = limits.min > 0 ? limits.min : 0.01;
    setInvoiceError(null);

    const setAmountError = (message: string) => {
      formikInstance?.setFieldTouched('amount', true, false);
      formikInstance?.setFieldError('amount', message);
    };

    formikInstance?.setFieldValue('currency', currency, false);

    const rawAmount = formikInstance?.values.amount;
    const parsedAmount =
      typeof rawAmount === 'number'
        ? rawAmount
        : typeof rawAmount === 'string'
          ? parseToNumber(rawAmount)
          : undefined;

    const formAmount =
      typeof parsedAmount === 'number' && Number.isFinite(parsedAmount) ? parsedAmount : undefined;

    if (formAmount === undefined || formAmount === null) {
      setAmountError(t('wallet.enterDepositAmount'));
      return;
    }

    if (formAmount <= 0) {
      setAmountError(t('wallet.amountGreaterThanZero'));
      return;
    }

    if (formAmount < effectiveMin) {
      setAmountError(getMinErrorText(effectiveMin));
      return;
    }

    if (limits.max !== undefined && formAmount > limits.max) {
      setAmountError(getMaxErrorText(limits.max));
      return;
    }

    formikInstance?.setFieldError('amount', undefined);

    try {
      setIsCreatingInvoice(true);
      setInvoiceError(null);

      const response = await clientAuthService.createPaymentInvoice({
        amount: formAmount,
        currency: currency,
      });

      if (response.data) {
        setPaymentInvoice(response.data);
      }
    } catch (error) {
      console.error('Ошибка при создании платежного инвойса:', error);
      setInvoiceError(t('wallet.failedCreateInvoice'));
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  // Функция для форматирования даты
  const formatDate = (dateString: string) => {
    // Если сервер отдает локальное время с 'Z', нужно добавить offset
    const utcDate = new Date(dateString);
    if (Number.isNaN(utcDate.getTime())) {
      return '';
    }
    const date = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.max(0, Math.floor(diffInMs / (1000 * 60 * 60 * 24)));
    const diffInHours = Math.max(
      0,
      Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    );

    if (diffInDays > 0) {
      return t('wallet.timeAgo.daysHours', { days: diffInDays, hours: diffInHours });
    }
    if (diffInHours > 0) {
      return t('wallet.timeAgo.hours', { hours: diffInHours });
    }
    const diffInMinutes = Math.max(0, Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60)));
    return t('wallet.timeAgo.minutes', { minutes: diffInMinutes });
  };

  const getActionType = (item: BalanceHistoryItem): WalletActionType => {
    if (item.transaction || !item.company) {
      return item.amount >= 0 ? 'balance_top_up' : 'invoice_payment';
    }

    const status = item.invoice?.status?.toLowerCase();
    switch (status) {
      case 'hold':
        // Резерв может быть только при отрицательном amount (блокировка средств)
        return item.amount < 0 ? 'reserve' : 'balance_top_up';
      case 'refunded':
        // Возврат резерва может быть только при положительном amount
        return item.amount >= 0 ? 'reserve_revert' : 'reserve';
      case 'paid':
        return 'campaign_complete';
      case 'guarantee':
        return 'guarantee';
      default:
        // Если положительная сумма - пополнение баланса, если отрицательная - оплата инвойса
        return item.amount >= 0 ? 'balance_top_up' : 'invoice_payment';
    }
  };

  const getAmountColor = (amount: number) => {
    return amount >= 0 ? 'text-auth-strength-strong' : 'text-auth-strength-weak';
  };

  // Функция для форматирования даты истечения
  const formatExpirationDate = (dateString: string) => {
    const date = new Date(dateString);
    const diffInMs = date.getTime() - currentTime.getTime();

    // Если время истекло, показываем 00:00
    if (diffInMs <= 0) {
      return '00:00';
    }

    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInSeconds = Math.floor((diffInMs % (1000 * 60)) / 1000);

    return `${diffInMinutes.toString().padStart(2, '0')}:${diffInSeconds.toString().padStart(2, '0')}`;
  };

  const columns = useMemo(
    () => [
      { name: t('wallet.id'), key: 'id' },
      { name: t('wallet.amount'), key: 'amount' },
      { name: t('wallet.action'), key: 'action' },
      { name: t('wallet.date'), key: 'date' },
    ],
    [t]
  );
  const getStatusChip = (actionType: WalletActionType) => {
    const config: Record<WalletActionType, { text: string; color: string }> = {
      reserve: { text: t('wallet.reserve'), color: '#3B82F6' },
      reserve_revert: { text: t('wallet.reserveRevert'), color: '#3B82F6' },
      campaign_complete: { text: t('wallet.campaignComplete'), color: '#17C964' },
      invoice_payment: { text: t('wallet.invoicePayment'), color: '#7828C8' },
      balance_top_up: { text: t('wallet.balanceTopUpAction'), color: '#F5A524' },
      guarantee: { text: t('wallet.guarantee'), color: '#17C964' },
      balance_change: { text: t('wallet.balanceChange'), color: '#71717A' },
    };

    return config[actionType] ?? config.balance_change;
  };

  const resolveOrderNumber = (item: BalanceHistoryItem) => {
    const invoiceAny = item.invoice as Record<string, any> | undefined;
    const orderAny = invoiceAny?.order as Record<string, any> | undefined;
    return getDisplayIdentifier(
      orderAny?.number,
      orderAny?.id,
      invoiceAny?.order_number,
      invoiceAny?.orderId,
      (item as Record<string, any>)?.order_number,
      item.company?.id
    );
  };

  const resolveInvoiceNumber = (item: BalanceHistoryItem): string | undefined => {
    const invoiceAny = item.invoice as Record<string, any> | undefined;
    return getDisplayIdentifier(
      item.invoice?.number,
      invoiceAny?.invoice_number,
      invoiceAny?.number,
      item.invoice?.id
    );
  };

  const resolvePaymentIdentifier = (item: BalanceHistoryItem): string | undefined => {
    const transactionAny = item.transaction as Record<string, any> | undefined;
    return getDisplayIdentifier(
      item.transaction?.admin,
      transactionAny?.admin,
      item.transaction?.id,
      transactionAny?.payment_number,
      item.transaction?.number,
      item.id
    );
  };

  const resolvePartnerHandle = (item: BalanceHistoryItem) => {
    const username = item.invoice?.partner?.username;
    if (typeof username === 'string' && username.trim()) {
      const trimmed = username.trim();
      return trimmed.startsWith('@') ? trimmed : `@${trimmed}`;
    }

    const invoiceAny = item.invoice as Record<string, any> | undefined;
    return extractHandleFromLink(
      item.company?.link ??
        invoiceAny?.company?.link ??
        (invoiceAny?.campaign as Record<string, any> | undefined)?.link
    );
  };


  const resolvePartnerLink = (item: BalanceHistoryItem) => {
    const invoiceAny = item.invoice as Record<string, any> | undefined;
    return pickFirstString(
      (item.invoice?.partner as Record<string, any> | undefined)?.link,
      invoiceAny?.partner_link,
      invoiceAny?.partner?.profile_link,
      invoiceAny?.partner?.link,
      invoiceAny?.company?.link,
      (invoiceAny?.campaign as Record<string, any> | undefined)?.link,
      item.company?.link
    );
  };

  const resolveDescription = (item: BalanceHistoryItem) => {
    const invoiceAny = item.invoice as Record<string, any> | undefined;
    return pickFirstString(
      item.description,
      item.comment,
      item.reason,
      item.details,
      item.note,
      invoiceAny?.reason,
      invoiceAny?.comment,
      invoiceAny?.status_reason,
      invoiceAny?.status_message,
      invoiceAny?.note
    );
  };

  const formatShortId = (value?: string) => {
    if (!value) {
      return undefined;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }

    if (trimmed.length <= 4) {
      return trimmed;
    }

    return trimmed.slice(-4);
  };

  const renderCell = (item: BalanceHistoryItem, columnKey: string) => {
    const actionType = getActionType(item);
    const statusChip = getStatusChip(actionType);

    switch (columnKey) {
      case 'id':
        return <IdView textToCopy={item.id}>{formatShortId(item.id) ?? item.id}</IdView>;
      case 'amount': {
        const isPositive = item.amount >= 0;
        const sign = isPositive ? '+' : '';
        const formattedAmount = Number.isFinite(item.amount)
          ? Math.abs(item.amount).toFixed(2)
          : '0.00';
        const formattedBefore = Number.isFinite(item.before) ? item.before.toFixed(2) : '0.00';
        const formattedAfter = Number.isFinite(item.after) ? item.after.toFixed(2) : '0.00';

        return (
          <div className="flex flex-col gap-1">
            <span className={`${getAmountColor(item.amount)} text-sm font-semibold`}>
              {sign}${formattedAmount}
            </span>
            <div className="flex items-center gap-1">
              <span className="text-auth-muted text-sm font-normal">${formattedBefore}</span>
              <DoubleArrowRightIc />
              <span className="text-auth-ink text-sm font-semibold">${formattedAfter}</span>
            </div>
          </div>
        );
      }
      case 'action': {
        const orderNumber = resolveOrderNumber(item);
        const companyId = item.company?.id ? String(item.company.id) : undefined;
        const invoiceIdFromItem = item.invoice?.id ? String(item.invoice.id) : undefined;
        const orderIdFull = orderNumber ?? companyId
        const orderDisplay =
          formatShortId(orderNumber) ??
          formatShortId(companyId) ??
          formatShortId(invoiceIdFromItem) ??
          formatShortId(item.id);
        const invoiceNumber = resolveInvoiceNumber(item);
        const invoiceIdResolved = invoiceNumber ? String(invoiceNumber) : undefined;
        const invoiceIdFull = invoiceIdResolved ?? item.invoice?.id ?? item.transaction?.id ?? item.id;
        const invoiceIdDisplay = formatShortId(invoiceIdResolved) ?? formatShortId(item.invoice?.id) ?? formatShortId(item.transaction?.id) ?? formatShortId(item.id);
        const paymentId = resolvePaymentIdentifier(item);
        const partnerHandle = resolvePartnerHandle(item);
        const partnerAvatar = item?.model?.avatar;
        const partnerLink = resolvePartnerLink(item);
        const description = resolveDescription(item);
        const actionsWithDetails: WalletActionType[] = [
          'reserve',
          'reserve_revert',
          'campaign_complete',
          'guarantee',
        ];
        const showOrderSection = actionsWithDetails.includes(actionType);
        const handleWithoutAt = partnerHandle?.startsWith('@')
          ? partnerHandle.slice(1)
          : partnerHandle;
        const modelHref =
          partnerLink || (handleWithoutAt ? `https://onlyfans.com/${handleWithoutAt}` : undefined);
        const modelLabel = partnerHandle;
        const shouldShowModel = Boolean(item?.model?.avatar);
        const showAvatar = Boolean(item?.model?.avatar);

        return (
          <div className="flex flex-col gap-1">
            <Chip
              size="sm"
              style={{
                backgroundColor: `${statusChip.color}20`,
                color: statusChip.color,
              }}
            >
              {statusChip.text}
            </Chip>
            {/* {actionType === 'invoice_payment' && invoiceIdFull && (
              <div className="flex flex-wrap items-center gap-[8px]">
                <div className="flex items-center gap-[6px]">
                  <span className="text-[#11181C] text-[14px] font-semibold">
                    {t('wallet.invoiceTitle')}
                  </span>
                  <IdView textToCopy={invoiceIdFull}>
                    {invoiceIdDisplay ?? invoiceIdFull}
                  </IdView>
                </div>
              </div>
            )} */}
            {actionType === 'balance_top_up' && paymentId && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-auth-ink text-sm font-semibold">
                  {t('wallet.paymentTitle')}
                </span>
                <IdView textToCopy={paymentId}>{formatShortId(paymentId) ?? paymentId}</IdView>
              </div>
            )}
            {showOrderSection && (orderIdFull || shouldShowModel) && (
              <div className="flex flex-wrap items-center gap-2">
                {orderIdFull && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-auth-ink text-sm font-semibold">
                      {t('wallet.orderTitle')}
                    </span>
                    <IdView textToCopy={orderIdFull}>
                      {orderDisplay ?? formatShortId(orderIdFull) ?? orderIdFull}
                    </IdView>
                  </div>
                )}
                {showAvatar && (
                  <Avatar
                    src={partnerAvatar}
                    size="sm"
                    showFallback
                    name={modelLabel?.slice(1, 2)}
                    className="w-4 h-4 rounded-1"
                  />
                )}
                {shouldShowModel &&
                  (modelHref ? (
                    <a
                      href={modelHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-partner text-sm font-normal"
                    >
                      {modelLabel}
                    </a>
                  ) : (
                    <span className="text-auth-muted text-sm font-normal">{modelLabel}</span>
                  ))}
              </div>
            )}
            {description && (
              <span className="text-auth-muted text-xs font-normal">{description}</span>
            )}
          </div>
        );
      }
      case 'date':
        return (
          <div className="flex items-center gap-2">
            <CalendarIcon />
            <span className="text-auth-muted text-xs">{formatDate(item.created_at)}</span>
          </div>
        );
      default:
        return null;
    }
  };

  const paymentMethods = [
    { id: 'usdttrc20', name: 'USDT (TRC20)', icon: <UsdcCoinIc fill="#9675CE" />, active: true },
    { id: 'eth', name: 'Ethereum', icon: <EthIc />, active: true },
  ];

  // Обработчики пагинации
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchBalanceHistory(page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении размера
    fetchBalanceHistory(1, size);
  };
  // Схема валидации для формы
  const getValidationSchema = () => {
    const { min, max } = currentLimits;
    const effectiveMin = min > 0 ? min : 0.01;

    return Yup.object().shape({
      amount: Yup.number()
        .typeError(t('wallet.amountRequired'))
        .required(t('wallet.amountRequired'))
        .moreThan(0, t('wallet.amountGreaterThanZero'))
        .test('amount-range', getMinErrorText(effectiveMin), function (value) {
          if (typeof value !== 'number' || Number.isNaN(value)) {
            return false;
          }

          if (value < effectiveMin) {
            return this.createError({
              message: getMinErrorText(effectiveMin),
            });
          }

          if (max !== undefined && value > max) {
            return this.createError({
              message: getMaxErrorText(max),
            });
          }

          return true;
        }),
    });
  };

  const customEmptyContent = useMemo(
    () => (
      <div className="text-center py-12 w-full">
        <div className="flex flex-col  items-center gap-4">
          <EmptyContentClientIc />
          <div className="flex flex-col  max-w-[27.5rem] gap-1.5">
            <Typography variant="h6" className="text-xl font-semibold leading-7">
              {t('wallet.emptyHistoryTitle')}
            </Typography>
            <Typography
              className="text-neutral-500 text-base font-medium leading-6"
              variant="body1"
            >
              {t('wallet.emptyHistoryDescription')}
            </Typography>
          </div>
        </div>
      </div>
    ),
    [t]
  );
  return (
    <div className="flex flex-col md:h-full px-4 pb-6 md:px-6 md:pb-4 gap-8 md:gap-4">
      <Typography className="pt-6 md:py-4" variant="h5">
        {t('wallet.accountBalance')}
      </Typography>
      <div className="flex h-full flex-col gap-6 md:gap-8">
        <Card className="border min-h-[5.25rem] border-gray-90 shadow-sm rounded-3.5">
          <CardBody className="p-4 flex overflow-hidden flex-col gap-1">
            <Typography
              className="!text-neutral-800 !font-medium !text-sm !leading-[1.125rem]"
              variant="h6"
            >
              {t('wallet.currentBalance')}
            </Typography>
            <Typography variant="h4" className="!font-semibold text-neutral-900 !text-2xl ">
              ${user?.balance?.toFixed(2) || '0.00'}
            </Typography>
          </CardBody>
        </Card>

        {/* Секция пополнения баланса */}
        <div className="flex flex-col gap-4">
          <Typography variant="h6" className="text-auth-ink font-medium text-xl">
            {t('wallet.balanceTopUp')}
          </Typography>

          <Card className="border border-gray-90 shadow-sm rounded-3.5">
            <CardBody className="p-4 flex flex-col gap-6">
              {/* Сумма депозита */}
              <Formik<DepositFormValues>
                innerRef={formikRef}
                initialValues={{
                  amount: undefined,
                  currency: selectedCurrency,
                }}
                validationSchema={getValidationSchema()}
                onSubmit={() => {
                  createPaymentInvoice(selectedCurrency);
                }}
              >
                {({ values, errors, touched, handleBlur, setFieldValue }) => (
                  <Form>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        <CustomHeroInput
                          label={t('wallet.depositAmount')}
                          type="text"
                          inputMode="decimal"
                          name="amount"
                          value={
                            values.amount === undefined || values.amount === null
                              ? ''
                              : values.amount
                          }
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            const parsedValue =
                              rawValue === '' ? undefined : parseToNumber(rawValue);
                            setInvoiceError(null);
                            setFieldValue('amount', parsedValue);
                          }}
                          onBlur={handleBlur}
                          startContent="$"
                          className="w-[9.4375rem]"
                          error={errors.amount}
                          touched={touched.amount}
                          infoIcon={false}
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>

              {/* Способ оплаты */}
              <div className="flex flex-col gap-2">
                <Typography variant="body1" className="text-auth-ink font-normal text-base">
                  {t('wallet.paymentMethod')}
                </Typography>

                <div className="flex flex-wrap gap-3">
                  {paymentMethods.map((method: any) => (
                    <CustomHeroButton
                      key={method.id}
                      title={method.name}
                      onPress={() => {
                        setSelectedCurrency(method.id);
                        createPaymentInvoice(method.id);
                      }}
                      startContent={method.icon}
                      disabled={!method.active || isCreatingInvoice}
                      variant={selectedCurrency === method.id ? 'secondary' : 'secondary'}
                    />
                  ))}
                </div>
              </div>

              {invoiceError && (
                <div className="text-auth-strength-weak text-sm text-center">{invoiceError}</div>
              )}
              {paymentInvoice && (
                <div className="flex flex-col gap-2">
                  <Typography variant="body1">{t('wallet.makePayment')}</Typography>

                  <Card className="border border-[#F1E8FF] shadow-sm rounded-3.5 bg-[#FAF8FF]">
                    <CardBody className="p-4 flex flex-col gap-4">
                      {/* Первая строка: Перевести сумму USDT на адрес */}
                      <div className="flex flex-col gap-2">
                        {/* @ts-ignore */}
                        <Typography
                          variant="body2"
                          className="text-auth-muted font-3.5 leading-5 font-regular"
                          dangerouslySetInnerHTML={{
                            __html: t('wallet.transferToAddress', {
                              amount: `<strong>${paymentInvoice.crypto_amount}</strong>`,
                              currency: paymentInvoice.crypto_currency.toUpperCase(),
                            }),
                          }}
                        ></Typography>

                        {/* Адрес для перевода в стиле дизайна */}
                        <Snippet
                          symbol={null}
                          tooltipProps={{
                            color: 'foreground',
                            content: 'Скопировать адрес',
                            disableAnimation: true,
                            placement: 'right',
                            closeDelay: 0,
                          }}
                          classNames={{
                            base: 'bg-[#F1E8FF] rounded-5 px-2 gap-0 py-0.5 w-fit',
                            pre: 'bg-transparent text-primary text-sm font-mono border-none',
                            copyButton:
                              'text-primary !min-w-[1.375rem] !max-w-4 h-4 hover:text-primary',
                            checkIcon: 'text-primary ',
                          }}
                        >
                          {paymentInvoice.crypto_address}
                        </Snippet>
                      </div>

                      {/* Информационный текст */}
                      <div className="max-w-[27.1875rem]">
                        <Typography
                          variant="body2"
                          className="text-auth-muted font-3.5 leading-5 font-regular"
                        >
                          <strong>{t('wallet.oneTimeAddress')}</strong> {t('wallet.availableFor')}{' '}
                          <strong>
                            {formatExpirationDate(paymentInvoice.expiration_estimate_date)}
                          </strong>{' '}
                          <br />
                          {t('wallet.autoRegistration')} <br />
                          {t('wallet.rateRecalculation')}
                        </Typography>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Блок с деталями платежа - переделан согласно дизайну */}
        </div>

        {/* Секция истории баланса */}
        <div className="flex flex-col gap-4 h-full">
          <div className="flex items-center justify-between">
            <Typography variant="h6" className="text-auth-ink font-medium text-xl">
              {t('wallet.balanceHistory')}
            </Typography>
          </div>

          {/* {isLoading ? (
                <div className="text-center py-12">
                  <div className="text-auth-muted">Обновление данных...</div>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-auth-strength-weak mb-4">{error}</div>
                  <CustomHeroButton
                    title="Повторить"
                    onPress={() => {
                      setHasError(false); // Сбрасываем флаг ошибки при повторной попытке
                      fetchBalanceHistory(currentPage, pageSize);
                    }}
                    size="sm"
                  />
                </div>
              ) : ( */}
          <UniversalTable
            mobileSize="md"
            key={lang}
            data={balanceHistoryData}
            columns={columns}
            renderCell={renderCell}
            emptyContent={customEmptyContent}
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            currentPage={currentPage}
            currentPageSize={pageSize}
          />
          {/* )} */}
        </div>
      </div>
    </div>
  );
};

export default Wallet;
